import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone } from "lucide-react";
import { KIKI } from "@/constants/testIds";
import { useT, useLang } from "@/lib/i18n";

const HERO_IMG = "https://images.unsplash.com/photo-1567789884554-0b844b597180?crop=entropy&cs=srgb&fm=jpg&w=1400&q=90";

export function Hero({ onEnquire }) {
  const ref = useRef(null);
  const t = useT();
  const { lang } = useLang();
  const isHi = lang === "hi";
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.06]);

  const metrics = t("hero.metrics");

  return (
    <section
      id="top"
      ref={ref}
      data-testid={KIKI.hero}
      className="relative pt-24 md:pt-28 pb-14 md:pb-20 overflow-hidden"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.5] dark:opacity-[0.35]"
        style={{
          backgroundImage: "radial-gradient(hsl(var(--muted-foreground) / 0.28) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
          maskImage: "linear-gradient(to bottom, black, transparent 75%)",
          WebkitMaskImage: "linear-gradient(to bottom, black, transparent 75%)",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-[90rem] mx-auto px-6 md:px-10">
        {/* Top ribbon */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col lg:flex-row lg:items-center gap-3 lg:justify-between mb-8 md:mb-14"
        >
          <div className={`inline-flex flex-wrap items-center gap-x-3 gap-y-2 bg-[#0A0A0A] text-white/90 rounded-full px-4 py-2 self-start ${isHi ? "font-hindi" : ""}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFCC00] animate-pulse" />
            <span className={`label-overline text-[10px] ${isHi ? "font-hindi tracking-normal text-[11px]" : ""}`}>{t("hero.ribbonOpen")}</span>
            <span className="w-px h-3 bg-white/25 hidden sm:inline" />
            <span className={`label-overline text-[10px] ${isHi ? "font-hindi tracking-normal text-[11px]" : ""}`}>{t("hero.ribbonPass")}</span>
            <span className="w-px h-3 bg-white/25 hidden sm:inline" />
            <span className={`label-overline text-[10px] ${isHi ? "font-hindi tracking-normal text-[11px]" : ""}`}>{t("hero.ribbonStreams")}</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-3 border border-border bg-card rounded-full px-4 py-2">
              <span className="grid place-items-center w-7 h-7 rounded-full bg-[#1a3a8a] text-white text-[10px] font-bold shrink-0">HK</span>
              <div className="text-left">
                <div className={`text-[13px] font-semibold leading-tight ${isHi ? "font-hindi" : ""}`}>{t("hero.partnerName")}</div>
                <div className={`text-[10px] text-muted-foreground leading-tight ${isHi ? "font-hindi" : ""}`}>{t("hero.partnerSub")}</div>
              </div>
            </div>
            <div className={`hidden md:inline-flex items-center gap-2 bg-[#0A0A0A] text-[#FFCC00] rounded-full px-4 py-2 label-overline text-[10px] rotate-[-4deg] origin-center ${isHi ? "font-hindi tracking-normal text-[11px]" : ""}`}>
              {t("hero.partnerRibbon")}
            </div>
          </div>
        </motion.div>

        {/* Headline + Photo */}
        <div className="grid grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="col-span-12 lg:col-span-7">
            <h1
              data-testid={KIKI.heroTitle}
              className={`font-black leading-[0.92] tracking-[-0.045em] ${isHi ? "font-hindi" : "font-sans"}`}
              style={{ fontSize: isHi ? "clamp(2.4rem, 7.2vw, 6rem)" : "clamp(2.9rem, 8.6vw, 7rem)" }}
            >
              <span className="mask-line">
                <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }} className="inline-block">
                  {t("hero.line1")}
                </motion.span>
              </span>
              <span className="mask-line">
                <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.28, ease: [0.16, 1, 0.3, 1] }} className={`inline-block ${isHi ? "font-hindi" : "font-display italic font-normal"}`}>
                  <span className="highlight-yellow">{t("hero.line2a")}</span>
                </motion.span>
              </span>
              <span className="mask-line">
                <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.42, ease: [0.16, 1, 0.3, 1] }} className={`inline-block ${isHi ? "font-hindi" : "font-display italic font-normal"}`}>
                  {t("hero.line2b")}
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className={`mt-7 md:mt-8 max-w-xl text-base md:text-lg text-foreground/75 leading-relaxed ${isHi ? "font-hindi" : ""}`}
            >
              {t("hero.lead")[0]}<span className="font-semibold text-foreground">{t("hero.lead")[1]}</span>{t("hero.lead")[2]}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3"
            >
              <button onClick={onEnquire} data-testid={KIKI.ctaEnquireHero} className={`btn-pill-yellow group ${isHi ? "font-hindi" : ""}`}>
                {t("hero.ctaBook")}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
              </button>
              <a href="#programmes" className={`btn-ghost-pill ${isHi ? "font-hindi" : ""}`}>{t("hero.ctaExplore")}</a>
              <a href="tel:+918800288994" className="btn-ghost-pill">
                <Phone className="w-4 h-4" strokeWidth={1.8} /> +91 88002 88994
              </a>
            </motion.div>
          </div>

          {/* Photo */}
          <div className="col-span-12 lg:col-span-5">
            <motion.div
              style={{ y: yImg, scale: scaleImg }}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="relative aspect-[4/3] lg:aspect-[5/4] w-full overflow-hidden rounded-2xl border border-border shadow-[0_20px_60px_-20px_rgba(0,0,0,0.25)]"
            >
              <img src={HERO_IMG} alt="KIKI student on precision machinery at Sector 75A campus" className="absolute inset-0 w-full h-full object-cover" />
              <div className={`absolute top-4 right-4 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full label-overline text-[10px] text-black ${isHi ? "font-hindi tracking-normal text-[11px]" : ""}`}>{t("hero.imgTag")}</div>
              <div className="absolute bottom-4 left-4 flex items-center gap-2">
                <div className="bg-white/95 backdrop-blur px-3 py-1.5 rounded-md">
                  <img src="/kiki-logo.png" alt="KIKI" className="h-5 w-auto select-none" draggable={false} />
                </div>
                <span className={`bg-black/70 text-white/90 px-3 py-1.5 rounded-md label-overline text-[10px] ${isHi ? "font-hindi tracking-normal text-[11px]" : ""}`}>{t("hero.imgLoc")}</span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 md:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4"
        >
          {metrics.map((m, i) => {
            const icons = ["◐", "◇", "₹", "◈"];
            return (
              <div key={i} className="flex items-center gap-3 md:gap-4 bg-card border border-border rounded-2xl px-4 md:px-5 py-4 card-hover">
                <div className="w-10 h-10 shrink-0 grid place-items-center bg-[#FFCC00] rounded-lg text-black text-lg font-bold">{icons[i]}</div>
                <div className="min-w-0">
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-sans text-xl md:text-[26px] font-black leading-none tracking-tight whitespace-nowrap">{m.k}</span>
                    <span className={`font-mono text-[10px] text-muted-foreground ${isHi ? "font-hindi" : ""}`}>{m.u}</span>
                  </div>
                  <span className={`label-overline text-muted-foreground text-[9px] md:text-[9.5px] block mt-1 truncate ${isHi ? "font-hindi tracking-normal text-[10px] md:text-[10.5px]" : ""}`}>{m.l}</span>
                </div>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
